import * as THREE from 'three';
import type { PhysicsCity } from '../physics/world';
import type { Person, Population, TrafficVehicle } from './population';

interface Driver {
  person: Person;
  car: TrafficVehicle;
  state: 'driving' | 'braking' | 'exiting' | 'sheltering' | 'returning' | 'boarding' | 'incapacitated';
  speed: number;
  retry: number;
  task?: string;
  door?: THREE.Vector3;
  transition?: { from: THREE.Vector3; to: THREE.Vector3; time: number };
}
const UPRIGHT = new THREE.Quaternion();
/** An ordinary car owns one of the existing residents as its driver. Alert
 * braking, physical disembarkation and later return all preserve that identity. */
export class CivilianDrivers {
  units: Driver[] = [];
  alert = false;
  time = 0;
  private epoch = 0;
  constructor(
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    for (const step of this.resetSteps()) void step;
  }
  clear(): void {
    this.units = [];
    this.alert = false;
    this.time = 0;
  }
  *resetSteps(): Generator<void> {
    this.clear();
    this.epoch = this.physics.life.epoch;
    const people = this.population.people.filter((p) => p.id.startsWith('person-')).reverse();
    const cars = this.population.cars.filter((c) => c.id.startsWith('traffic-'));
    for (const [i, car] of cars.entries()) {
      const person = people[i];
      if (!person) break;
      this.population.civil?.resume(person, 'street');
      person.carrier = car.id;
      person.controlled = true;
      person.exposedSeat = false;
      person.state = 'sitting';
      const routine = this.population.civil?.routines.get(person.id);
      if (routine) routine.intent = `Driving the original ${car.name ?? car.kind}`;
      this.units.push({ person, car, state: 'driving', speed: car.speed, retry: 0 });
      if (i % 16 === 15) yield;
    }
    this.syncSeats();
  }
  private seat(u: Driver): THREE.Vector3 {
    return new THREE.Vector3(-0.4, 0.18, -Math.min(0.65, u.car.length * 0.13))
      .applyQuaternion(u.car.q)
      .add(u.car.p);
  }
  private doors(u: Driver): THREE.Vector3[] {
    const car = u.car;
    return [-1, 1].map((side) =>
      car.p.clone().add(new THREE.Vector3(side * (car.width / 2 + 0.7), 0, -0.45).applyQuaternion(car.q)),
    );
  }
  private exit(u: Driver): boolean {
    for (const point of this.doors(u)) {
      const floor = this.physics.ground(point, 0.7, 1);
      if (!floor || Math.abs(floor.y - u.car.p.y) > 0.7) continue;
      point.y = floor.y;
      const network = this.population.civil?.network;
      if (
        !network?.near(point, 8, 'street').some(
          (node) =>
            network.lineOpen(point, node) &&
            this.physics.sweepFoot(point, {
              x: node.x - point.x,
              y: node.y - point.y,
              z: node.z - point.z,
            }) >= 0.999,
        ) ||
        !this.population.footClear(u.person, point) ||
        !this.physics.occupiedBoxClear(
          { x: point.x, y: point.y + 0.8, z: point.z },
          { x: 0.24, y: 0.74, z: 0.24 },
          UPRIGHT,
          { x: 0, y: 0, z: 0 },
          u.car.body,
        )
      )
        continue;
      if (
        !this.physics.occupiedBoxClear(
          { x: u.person.p.x, y: u.person.p.y + 0.8, z: u.person.p.z },
          { x: 0.23, y: 0.73, z: 0.23 },
          UPRIGHT,
          point.clone().sub(u.person.p),
          u.car.body,
        )
      )
        continue;
      u.door = point;
      u.transition = { from: u.person.p.clone(), to: point, time: 0 };
      u.state = 'exiting';
      return true;
    }
    return false;
  }
  private transition(u: Driver, dt: number): void {
    const t = u.transition!;
    if (u.state === 'boarding') t.to.copy(this.seat(u));
    const time = Math.min(0.8, t.time + dt),
      point = t.from.clone().lerp(t.to, time / 0.8),
      delta = point.clone().sub(u.person.p);
    if (
      !this.physics.occupiedBoxClear(
        { x: u.person.p.x, y: u.person.p.y + 0.8, z: u.person.p.z },
        { x: 0.23, y: 0.73, z: 0.23 },
        UPRIGHT,
        delta,
        u.car.body,
      )
    )
      return;
    t.time = time;
    u.person.previous.copy(u.person.p);
    u.person.p.copy(point);
    u.person.seatHeight = undefined;
    u.person.state = 'walk';
    if (time < 0.8) return;
    u.transition = undefined;
    if (u.state === 'boarding') {
      u.state = 'driving';
      u.person.carrier = u.car.id;
      u.person.controlled = true;
      u.car.controlled = false;
      u.car.state = 'driving';
      u.car.speed = u.speed;
    } else {
      u.state = 'sheltering';
      u.person.carrier = undefined;
      u.person.velocity.set(0, 0, 0);
      this.population.civil?.resume(u.person, 'street');
    }
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) return;
    this.time += dt;
    const civil = this.population.civil;
    const planningStart = performance.now();
    let returnPlans = 0;
    for (const u of this.units) {
      const { person, car } = u;
      if (car.state === 'wreck' && person.carrier === car.id) {
        person.carrier = undefined;
        person.controlled = false;
        person.seatHeight = undefined;
        u.transition = undefined;
        if (!person.fatal) this.population.incapacitate(person, car.p, 2.4);
        u.state = 'incapacitated';
      }
      if (person.fatal || ['incapacitated', 'falling', 'carried', 'recovering'].includes(person.state)) {
        car.speed = 0;
        car.controlled = true;
        person.carrier = undefined;
        u.state = 'incapacitated';
        continue;
      }
      if (u.transition) {
        this.transition(u, dt);
        continue;
      }
      if (u.state === 'driving' && this.alert) {
        u.state = 'braking';
        car.controlled = true;
        car.speed = 0;
      }
      if (u.state === 'braking') {
        const junction =
          car.junction === undefined ? undefined : this.population.network.nodes.get(car.junction);
        if (
          junction &&
          Math.hypot(car.p.x - junction.x, car.p.z - junction.z) <
            Math.max(
              4,
              ...(this.population.network.adjacent.get(junction.id) ?? []).map((e) => e.width / 2),
            ) +
              car.length / 2 +
              2
        ) {
          // Clear the junction already occupied by this original driver before
          // stopping. Parking across its pedestrian crossing traps both the
          // evacuating resident and every later return journey.
          car.controlled = false;
          car.speed = Math.min(u.speed, 4);
          continue;
        }
        car.controlled = true;
        car.speed = 0;
        if (car.actualSpeed >= 0.05) continue;
        car.state = 'parked';
        car.plan = [];
        if (this.time >= u.retry) {
          u.retry = this.time + 1;
          this.exit(u);
        }
      }
      if (u.state === 'sheltering' && !this.alert && civil && this.time >= u.retry) {
        // Original drivers remain safely stopped while their return route is
        // admitted. Do not issue hundreds of local door searches on Cancel.
        if (returnPlans >= 4 || performance.now() - planningStart > 0.7) continue;
        returnPlans++;
        u.retry = this.time + 3;
        if (car.state === 'wreck' || !civil.prepareForTask(person)) continue;
        for (const door of this.doors(u).sort(
          (a, b) => a.distanceToSquared(person.p) - b.distanceToSquared(person.p),
        )) {
          const node = civil.network
            .near(door, 6, 'street')
            .filter(
              (n) =>
                civil.network.lineOpen(n, door) &&
                this.physics.sweepFoot(n, { x: door.x - n.x, y: door.y - n.y, z: door.z - n.z }) >= 0.999,
            )
            .sort(
              (a, b) => Math.hypot(a.x - door.x, a.z - door.z) - Math.hypot(b.x - door.x, b.z - door.z),
            )[0];
          if (!node) continue;
          const token = `driver-return:${person.id}:${this.time}`;
          if (!civil.assignTask(person, civil.nodePlace(node), token, 'returning to parked vehicle', 0.1))
            continue;
          u.task = token;
          u.door = door;
          u.state = 'returning';
          break;
        }
      }
      if (u.state === 'returning' && civil) {
        if (this.alert) {
          civil.cancelTask(person, 'Renewed alert');
          u.state = 'sheltering';
          continue;
        }
        const task = civil.task(person);
        if (!task || task.token !== u.task || task.state === 'failed') {
          u.state = 'sheltering';
          continue;
        }
        if (task.state !== 'completed') continue;
        person.controlled = true;
        if (!this.population.footClear(person, u.door!)) continue;
        this.population.walkTo(person, u.door!, dt, 1.5);
        if (person.p.distanceTo(u.door!) < 0.25) {
          civil.cancelTask(person, 'Driver reached the original parked door');
          u.state = 'boarding';
          u.transition = { from: person.p.clone(), to: this.seat(u), time: 0 };
        }
      }
    }
  }
  syncSeats(): void {
    for (const u of this.units)
      if (['driving', 'braking'].includes(u.state) && u.person.carrier === u.car.id && !u.person.fatal) {
        u.person.previous.copy(u.person.p);
        u.person.p.copy(this.seat(u));
        u.person.yaw = u.car.yaw;
        u.person.state = 'sitting';
        u.person.seatHeight = 0.46;
        u.person.hidden = false;
        u.person.controlled = true;
      }
  }
  snapshot(): Record<string, number> {
    return {
      ordinaryDrivers: this.units.length,
      driversAboard: this.units.filter((u) => u.state === 'driving' || u.state === 'braking').length,
      driversSeekingRefuge: this.units.filter((u) => u.state === 'sheltering').length,
      abandonedCivilianVehicles: this.units.filter(
        (u) =>
          ['exiting', 'sheltering', 'returning', 'boarding'].includes(u.state) && u.car.state !== 'wreck',
      ).length,
    };
  }
  inspect(): unknown {
    return this.units.map((u) => ({
      person: u.person.id,
      car: u.car.id,
      state: u.state,
      position: u.person.p.toArray(),
      speed: u.car.actualSpeed,
      reason: u.state === 'driving' ? 'Original resident driving the assigned vehicle' : u.state,
    }));
  }
}
